import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Defaults to « Retour », the wording of the frames. */
  cancelLabel?: string;
  /** Request in flight: the confirm button spins and the dialog cannot be dismissed. */
  isPending?: boolean;
  onConfirm: () => void;
}

/**
 * « Annuler l'invitation ? », « Archiver la cohorte ? »: a question, a back button and the
 * confirming action. A thin wrapper on `Dialog`, which owns focus trap, Escape and the × button.
 *
 * The confirm button is the normal primary, not a separate red variant: that is how Raouf drew
 * « Annuler l'invitation » (Figma 1028:1522). The caller closes the dialog when the request
 * succeeds, so a failure leaves it open with the button ready again.
 */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isPending = false,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation("common");

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={!isPending}
      isKeyboardDismissDisabled={isPending}
      title={title}
      hasCloseButton
      actions={
        <>
          <Button
            variant="secondary"
            onPress={() => onOpenChange(false)}
            isDisabled={isPending}
          >
            {cancelLabel ?? t("actions.back")}
          </Button>
          <Button
            onPress={onConfirm}
            isPending={isPending}
            isDisabled={isPending}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description ? (
        <p className="text-md-regular text-(--text-secondary)">{description}</p>
      ) : null}
    </Dialog>
  );
}

export type { ConfirmDialogProps };
